import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api from "../lib/apiClient";
import { useAuth } from "../context/AuthContext";

// All data-loading + admin actions for the admin/super-admin console.
export function useAdminData() {
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const [tab, setTab] = useState("dashboard");
  const [txns, setTxns] = useState([]);
  const [review, setReview] = useState({ transactions: [], flags: [] });
  const [reds, setReds] = useState([]);
  const [merchants, setMerchants] = useState([]);
  const [ledger, setLedger] = useState([]);
  const [report, setReport] = useState(null);
  const [audit, setAudit] = useState([]);
  const [kycM, setKycM] = useState([]);
  const [cfg, setCfg] = useState(null);
  const [tree, setTree] = useState(null);
  const [users, setUsers] = useState([]);
  const [role, setRole] = useState({ email: "", role: "merchant" });
  const [selMerchant, setSelMerchant] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadAll = useCallback(async () => {
    api.get("/admin/transactions").then((r) => setTxns(r.data.transactions)).catch(() => {});
    api.get("/admin/review-queue").then((r) => setReview(r.data)).catch(() => {});
    api.get("/admin/redemptions").then((r) => setReds(r.data.redemptions)).catch(() => {});
    api.get("/admin/merchants").then((r) => setMerchants(r.data.merchants)).catch(() => {});
    api.get("/admin/budget-ledger").then((r) => setLedger(r.data.entries)).catch(() => {});
    api.get("/admin/audit").then((r) => setAudit(r.data.logs)).catch(() => {});
    api.get("/admin/campaign/report").then((r) => setReport(r.data)).catch(() => {});
    api.get("/admin/kyc").then((r) => setKycM(r.data.merchants)).catch(() => {});
    if (isSuper) {
      api.get("/admin/config").then((r) => setCfg(r.data)).catch(() => {});
      api.get("/admin/campaign/tree").then((r) => setTree(r.data)).catch(() => {});
      api.get("/admin/users").then((r) => setUsers(r.data.users)).catch(() => {});
    }
  }, [isSuper]);
  useEffect(() => { loadAll(); }, [loadAll]);

  const reverse = async (txn_id) => {
    const reason = prompt("Reason for reversal?");
    if (!reason) return;
    try { const { data } = await api.post("/admin/reverse", { txn_id, reason }); toast.success(`Reversed → ${data.status}`); await loadAll(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const processRedeem = async (redemption_id, status) => {
    try { await api.post("/admin/redemptions/process", { redemption_id, status }); toast.success(`Redemption ${status}`); await loadAll(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const viewDoc = async (doc_id) => {
    try {
      const res = await api.get(`/admin/kyc/file/${doc_id}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error("Could not open document"); }
  };
  const reviewKyc = async (merchant_id, decision) => {
    let note = "";
    if (decision === "REJECTED") { note = prompt("Reason for rejection?") || ""; }
    try { await api.post("/admin/kyc/review", { merchant_id, decision, note }); toast.success(`KYC ${decision}`); await loadAll(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const saveConfig = async () => {
    setBusy(true);
    try {
      await api.put("/admin/config", {
        gst_bps: Number(cfg.gst_bps), cashback_bps: Number(cfg.cashback_bps),
        hold_period_days: Number(cfg.hold_period_days), max_cashback_paise: Number(cfg.max_cashback_paise),
        min_redeem_paise: Number(cfg.min_redeem_paise),
        cashback_outcomes: (cfg.cashback_outcomes || []).map((o) => ({
          amount_paise: Number(o.amount_paise), weight_bps: Number(o.weight_bps) })),
      });
      toast.success("Config saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  const freeze = async () => {
    if (!window.confirm("Freeze current campaign and start a new cycle?")) return;
    try { await api.post("/admin/campaign/freeze"); toast.success("Campaign frozen; new cycle created"); await loadAll(); }
    catch { toast.error("Failed"); }
  };
  const assignRole = async () => {
    try { await api.post("/admin/users/role", role); toast.success("Role updated"); await loadAll(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const resetDemoData = async () => {
    if (!window.confirm("Permanently DELETE all demo/test + operational data (merchants, customers, tokens, subscriptions, transactions, draws). Config, plans and staff accounts are kept. This cannot be undone. Continue?")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/maintenance/reset-demo-data", { confirm: "RESET" });
      const c = Object.values(data.cleared || {}).reduce((s, n) => s + n, 0);
      toast.success(`Reset complete — ${c} records cleared. Fresh campaign created.`);
      await loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Reset failed"); } finally { setBusy(false); }
  };

  return {
    isSuper, tab, setTab, txns, review, reds, merchants, ledger, report, audit, kycM,
    cfg, setCfg, tree, users, role, setRole, selMerchant, setSelMerchant, busy,
    loadAll, reverse, processRedeem, viewDoc, reviewKyc, saveConfig, freeze, assignRole, resetDemoData,
  };
}
